/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type PipeTransform, Pipe } from '@angular/core';
import { AdaptUnitPipe, TimeService } from '@dm/core';

import { AnalysisStrService } from '../../monitor/analysis/analysis-str.service';
import {
    isBooleanGrammar,
    isDateGrammar,
    isEnumGrammar,
    isNumGrammar,
    isPerGrammar,
    isStringGrammar,
} from './global-infigo/conditionalSearch/grammar.type';
import { grammar, IgnoreCaseMatch } from './global-infigo/grammar/gammar';
import { InfigoService } from './global-infigo/infigo.service';
import { SourceEnumService } from './global-infigo/source-enum.service';

const keyMap = {
    LUNS: 'LUN',
    LUNGROUPS: 'LUNGROUP',
    NamespaceS: 'LUN',
    NamespaceGROUPS: 'LUNGROUP',
    HOSTS: 'HOST',
    HOSTGROUPS: 'HOSTGROUP',
    PORTGROUPS: 'PORTGROUP',
    LOGICALPORTS: 'LOGICPORT',
    VSTORES: 'TENANT',
    LUNSNAPSHOTS: 'LUNSNAPSHOT',
    FILESYSTEMS: 'FILESYSTEM',
};

@Pipe({
    name: 'condition',
})
export class ConditionPipe implements PipeTransform {
    constructor(
        public enumService: SourceEnumService,
        public adapt: AdaptUnitPipe,
        public infigoService: InfigoService,
        public analysisStrService: AnalysisStrService,
        public time: TimeService,
    ) {
    }

    /**
     * 条件语法下转换资源提示项
     * @param item 资源信息
     * @param type 资源类型
     * @param url 对应的后端接口地址 如 lun.get
     * @param field 条件语法词条 如LUN 容量>10 对应为 CAPACITY
     */
    transform(item: any, { type, url, field }): any {
        const { item: value } = item;
        if (!field) {
            return item.objectDes;
        }
        const val = value[field];
        // 忽视大小写匹配条件语法，对于不同语法做出不同处理
        const [match] = IgnoreCaseMatch(type, grammar);
        const matchGrammar = match[url].find((i) => i.value === field);
        if (isNumGrammar(matchGrammar)) {
            if (matchGrammar.format) {
                return matchGrammar.format(val);
            }
            if (matchGrammar.unit) {
                return this.adapt.transform(val, matchGrammar.unit);
            }
            return val;
        }
        if (isEnumGrammar(matchGrammar) || isBooleanGrammar(matchGrammar)) {
            return this.enumService.get(keyMap[type] || type.toUpperCase(), field)[val];
        }
        if (isStringGrammar(matchGrammar) && !/^name$/i.test(matchGrammar.value as string)) {
            return val;
        }
        if (isPerGrammar(matchGrammar)) {
            const { indicator_values, indicator } = value;
            const { unit } = this.analysisStrService.getIndicatorNameAndUnit(indicator);
            if (!unit) {
                return item.objectDes;
            }
            if (!indicator_values) {
                return this.infigoService.adaptValue(0, unit);
            }
            const computeData = indicator_values.filter((i) => !Number.isNaN(i));
            const length = computeData.length;
            const computeValue = length ? computeData.reduce((pre, cur) => Number(cur) + pre, 0) / length : 0;
            return this.infigoService.adaptValue(computeValue, unit).join(' ');
        }
        if (isDateGrammar(matchGrammar)) {
            return this.time.formatUTC(val * 1e3, 'YYYY-MM-DD HH:mm:ss');
        }
        return item.objectDes;
    }
}
